import { action, computed, observable, set } from 'mobx';
import Constants, { ADDRESS_TYPE_ID, FIELD_TYPE, INVOICE_CARRIER_TYPES } from '../../../constants/Constants';
import { v4 as uuidv4 } from 'uuid';

export type AddressId = string;

export interface IAddressAttributes {
  [FIELD_TYPE.ADDRESS_SEARCH]?: string;
  [FIELD_TYPE.ADDRESS_TYPE]?: ADDRESS_TYPE_ID;
  [FIELD_TYPE.BAD_FIELDS]?: Array<string>;
  [FIELD_TYPE.BILL_TO_COMPANY]?: boolean;
  [FIELD_TYPE.CITY]?: string;
  [FIELD_TYPE.COLONY]?: string;
  [FIELD_TYPE.COMPANY_ADDRESS]?: string;
  [FIELD_TYPE.COMPANY_NAME]?: string;
  [FIELD_TYPE.COMPANY_TAX_NUMBER]?: string;
  [FIELD_TYPE.COUNTRY_ID]?: number;
  [FIELD_TYPE.COUNTRY]?: string;
  [FIELD_TYPE.CREATED_DATE]?: string;
  [FIELD_TYPE.DEFAULT_SHIPPING]?: boolean;
  [FIELD_TYPE.DELIVERY_INSTRUCTIONS]?: string;
  [FIELD_TYPE.DISTRICT]?: string;
  [FIELD_TYPE.FIFTH_LINE_ADDRESS]?: string;
  [FIELD_TYPE.FIRST_LINE_ADDRESS]?: string;
  [FIELD_TYPE.FIRST_NAME_ALTERNATE]?: string;
  [FIELD_TYPE.FIRST_NAME]?: string;
  [FIELD_TYPE.FOURTH_LINE_ADDRESS]?: string;
  [FIELD_TYPE.FULL_NAME]?: string;
  [FIELD_TYPE.ID]?: string;
  [FIELD_TYPE.INVOICE_CARRIER_TYPE]?: INVOICE_CARRIER_TYPES;
  [FIELD_TYPE.INVOICE_CARRIER_ID]?: string;
  [FIELD_TYPE.INVOICE_DONATION_LOVE_CODE]?: string;
  [FIELD_TYPE.LAST_NAME_ALTERNATE]?: string;
  [FIELD_TYPE.LAST_NAME]?: string;
  [FIELD_TYPE.MUNICIPALITY]?: string;
  [FIELD_TYPE.PHONE]?: string;
  [FIELD_TYPE.PHONE1_TYPE]?: number;
  [FIELD_TYPE.PHONE2_TYPE]?: number;
  [FIELD_TYPE.SECOND_LINE_ADDRESS]?: string;
  [FIELD_TYPE.SECOND_PHONE]?: string;
  [FIELD_TYPE.STATE]?: string;
  [FIELD_TYPE.THIRD_LINE_ADDRESS]?: string;
  [FIELD_TYPE.UPDATED_DATE]?: string;
  [FIELD_TYPE.ZIPCODE]?: string;
  [FIELD_TYPE.FULL]?: string;
  [FIELD_TYPE.SHIP_TYPE]?: number;
}

class Address {
  @observable public id: AddressId = Address.NEW_ADDRESS_ID;

  @observable public readonly attributes: IAddressAttributes = {};

  @observable public clientId: string = uuidv4();

  public static NEW_ADDRESS_ID = '';

  constructor() {
    this.attributes = {
      [FIELD_TYPE.COUNTRY]: '',
      [FIELD_TYPE.COUNTRY_ID]: null,
      [FIELD_TYPE.PHONE]: '',
      [FIELD_TYPE.SECOND_PHONE]: '',
    };
  }

  public static fromJSON(data: IAddressAttributes): Address {
    const address = new Address();
    address.id = data[FIELD_TYPE.ID];

    set(address.attributes, data);

    return address;
  }

  public static isNewAddress(id: string): boolean {
    return !id;
  }

  @action
  public setDefault(isDefault: boolean): void {
    this.updateAttributes({ [FIELD_TYPE.DEFAULT_SHIPPING]: isDefault });
  }

  public static toJSON(address: Address): IAddressAttributes {
    return address.attributes;
  }

  @computed
  get isDefault(): boolean {
    return this.attributes[FIELD_TYPE.DEFAULT_SHIPPING];
  }

  // We have this property because the address could be new
  // but been set at the same time. This is for scenarios like for Paypal when it misses some field
  // where the BE caches a temporary address that we get on the FE without an id.
  @computed
  get isSet(): boolean {
    return !!this.attributes[FIELD_TYPE.COUNTRY_ID];
  }

  @computed
  get isRestrictedAddress(): boolean {
    const city: string = this.attributes.city.toUpperCase();

    return city === Constants.APO
      || city === Constants.DPO
      || city === Constants.FPO;
  }

  @computed
  public get createdDate(): Date | null {
    const createdDateField = this.attributes[FIELD_TYPE.CREATED_DATE] as string | undefined;
    return createdDateField ? new Date(createdDateField) : null;
  }

  @computed
  public get updatedDate(): Date | null {
    const updatedDateField = this.attributes[FIELD_TYPE.UPDATED_DATE] as string | undefined;
    return updatedDateField ? new Date(updatedDateField) : null;
  }

  @action
  public compare = (addressToCompare: Address): number => {
    const a1IsDefault = this.attributes[FIELD_TYPE.DEFAULT_SHIPPING];
    const a2IsDefault = addressToCompare.attributes[FIELD_TYPE.DEFAULT_SHIPPING];
    const a1UpdatedDate = this.updatedDate;
    const a2UpdatedDate = addressToCompare.updatedDate;
    const a1CreatedDate = this.createdDate;
    const a2CreatedDate = addressToCompare.createdDate;

    if (a1IsDefault) {
      return -1;
    }

    if (a2IsDefault) {
      return 1;
    }

    const d1 = a1UpdatedDate || a1CreatedDate;
    const d2 = a2UpdatedDate || a2CreatedDate;

    if (d1 && d2) {
      return d2.getTime() - d1.getTime();
    }

    return `${this.id}`.localeCompare(`${addressToCompare.id}`);
  };

  public get isNewAddress(): boolean {
    return Address.isNewAddress(this.id);
  }

  public get isSavedPackstation(): boolean {
    return !this.isNewAddress
      && this.attributes[FIELD_TYPE.ADDRESS_TYPE] === ADDRESS_TYPE_ID.PACKSTATION;
  }

  @action
  public updateAttributes = (attributes: IAddressAttributes): void => {
    set(this.attributes, attributes);
  };
}

export default Address;
